import { useLazyQuery } from '@vue/apollo-composable';
import { useDict, useFischer } from 'composables/fischerPlugin';
import { useUser } from 'composables/user';
import GET_CONSTRUCTION_MONITORING_AREA from 'gql/getConstructionMonitoringArea.gql';
import GET_CONSTRUCTION_MONITORING_AREA_LIST from 'gql/getConstructionMonitoringAreaList.gql';
import GET_CONSTRUCTION_ROOM from 'gql/getConstructionRoom.gql';
import GET_CONSTRUCTION_ROOM_LIST from 'gql/getConstructionRoomList.gql';
import GET_CONSTRUCTION_SENSOR_BOLT_SUMMARY from 'gql/getConstructionSensorBolts.gql';
import GET_CONSTRUCTION_SENSOR_DISC_SUMMARY from 'gql/getConstructionSensorDiscs.gql';
import GET_CONSTRUCTION_SITE from 'gql/getConstructionSite.gql';
import { useConstructionMonitoringStore } from 'store/construction-monitoring';
import { computed } from 'vue';

export const useConstructionMonitoring = () => {
  const projectType = 'cm';
  const fischer = useFischer();
  const dict = useDict();
  const cmStore = useConstructionMonitoringStore();
  const { logoutIfTokenInvalid } = useUser();

  const pageUrl = fischer.pages.constructionMonitoring()?.url;

  const setBrowserPageTitle = (pageTitle: string | undefined) => {
    if (IS_SSR) {
      return;
    }

    const title = document.querySelector('head title');
    if (title) {
      if (pageTitle) {
        title.textContent = `${pageTitle} - ${dict.get('construction-Projects.Breadcrumb.Construction-Monitoring')} | ${fischer.brandName()}`;
      } else {
        title.textContent = `${dict.get('construction-Projects.Breadcrumb.Construction-Monitoring')} | ${fischer.brandName()}`;
      }
    }
  };

  const formatNameForUrl = (name: string) => {
    return name
      .replace(/[^A-Za-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+$/, '')
      .toLowerCase();
  };

  const getPathString = (pageType: string) => {
    const data: any = cmStore[pageType as keyof typeof cmStore];
    if (data) {
      const name = formatNameForUrl(data?.name);
      const id = cmStore[`${pageType}Id` as keyof typeof cmStore];
      if (typeof id !== 'string') {
        console.error('missing id for pageType', pageType);
        return '';
      }
      return encodeURI(`/${name}_${id.replace(/.*:/, '')}`);
    }
    return '';
  };

  const getCMUrl = (
    targetName?: string | null,
    targetId?: string | null,
    targetType?: string | null,
  ) => {
    if (!targetId || !targetName) {
      return '';
    }

    const name = formatNameForUrl(targetName);
    const id = targetId.replace(/.*:/, '');

    if (targetType === 'area') {
      return pageUrl + getPathString('project') + encodeURI(`/${name}_${id}`);
    }

    if (targetType === 'assembly-group' || targetType === 'room') {
      return (
        pageUrl + getPathString('project') + getPathString('area') + encodeURI(`/${name}_${id}`)
      );
    }

    return pageUrl + encodeURI(`/${name}_${targetId}`);
  };

  const graphQlVariables = computed(() => {
    return {
      siteId: cmStore.projectId,
      areaId: cmStore.areaId,
      roomId: cmStore.roomId,
      impersonatedUser: fischer.user.getAssistedServiceEmail(),
      accessToken: fischer.user.accessToken(),
      language: fischer.siteLanguage(),
      project: projectType,
    };
  });

  const {
    load: loadProject,
    onError: onProjectError,
    refetch: doRefetchProject,
    loading: projectLoading,
  } = useLazyQuery(GET_CONSTRUCTION_SITE, graphQlVariables.value);

  onProjectError((err: any) => {
    console.error('could not load construction monitoring project', err);

    const { graphQLErrors } = err;
    if (graphQLErrors.some((e: any) => e.extensions.code === 'NOT_PERMITTED')) {
      window.location.href = fischer.pages.constructionMonitoring().url;
      return;
    }

    logoutIfTokenInvalid(err);
  });

  const {
    load: loadArea,
    onError: onAreaError,
    refetch: doRefetchArea,
    loading: areaLoading,
  } = useLazyQuery(GET_CONSTRUCTION_MONITORING_AREA, graphQlVariables.value);

  onAreaError((err: any) => {
    console.error('could not load construction monitoring area', err);
  });

  const {
    load: loadAreaList,
    onError: onAreaListError,
    refetch: doRefetchAreaList,
    loading: areaListLoading,
  } = useLazyQuery(GET_CONSTRUCTION_MONITORING_AREA_LIST, graphQlVariables.value);

  onAreaListError((err: any) => {
    console.error('could not load construction monitoring area list', err);
  });

  const {
    load: loadRoom,
    onError: onRoomError,
    refetch: doRefetchRoom,
    loading: roomLoading,
  } = useLazyQuery(GET_CONSTRUCTION_ROOM, graphQlVariables.value);

  onRoomError((err: any) => {
    console.error('could not load construction monitoring assembly group', err);
  });

  const {
    load: loadRoomList,
    onError: onRoomListError,
    refetch: doRefetchRoomList,
    loading: roomListLoading,
  } = useLazyQuery(GET_CONSTRUCTION_ROOM_LIST, graphQlVariables.value);

  onRoomListError((err: any) => {
    console.error('could not load construction monitoring assembly group list', err);
  });

  const {
    load: loadSensorBolts,
    onError: onSensorBoltSummaryError,
    refetch: refetchSensorBolts,
    loading: sensoBoltSummaryLoading,
  } = useLazyQuery(GET_CONSTRUCTION_SENSOR_BOLT_SUMMARY, graphQlVariables.value);

  onSensorBoltSummaryError((err: any) => {
    console.error('could not load construction monitoring sensor bolt summary', err);
  });

  const loadSensorBoltSummary = async () => {
    try {
      const { sensorBoltSummary } = await loadSensorBolts();
      cmStore.sensorBoltSummary = sensorBoltSummary.items;
    } catch (error: any) {
      console.error(error);
    }
  };

  const {
    load: loadSensorDiscs,
    onError: onSensorDiscSummaryError,
    refetch: refetchSensorDiscs,
    loading: sensoDiscSummaryLoading,
  } = useLazyQuery(GET_CONSTRUCTION_SENSOR_DISC_SUMMARY, graphQlVariables.value);

  onSensorDiscSummaryError((err: any) => {
    console.error('could not load construction monitoring sensor disc summary', err);
  });

  const loadSensorDiscSummary = async () => {
    try {
      const { sensorDiscSummary } = await loadSensorDiscs();
      cmStore.sensorDiscSummary = sensorDiscSummary.items;
    } catch (error: any) {
      console.error(error);
    }
  };

  const loadConstructionMonitoringData = async () => {
    const promises = [];

    if (cmStore.projectId) {
      promises.push(loadProject());
      promises.push(loadAreaList());
    } else {
      promises.push(null, null);
    }

    if (cmStore.areaId) {
      promises.push(loadArea());
      promises.push(loadRoomList());
    } else {
      promises.push(null, null);
    }

    if (cmStore.roomId) {
      promises.push(loadRoom());
    } else {
      promises.push(null);
    }

    if (!promises.length) {
      return;
    }

    try {
      cmStore.isLoading = true;
      const [project, areaList, area, roomList, room] = await Promise.all(promises);

      if (areaList?.constructionMonitoringAreaList?.areas) {
        cmStore.project = {
          ...project?.constructionSite,
          areas: areaList.constructionMonitoringAreaList.areas,
        };
      } else {
        cmStore.project = project?.constructionSite;
      }

      if (roomList?.constructionRoomList?.rooms) {
        cmStore.area = {
          ...area?.constructionMonitoringArea,
          rooms: roomList.constructionRoomList.rooms,
        };
      } else {
        cmStore.area = area?.constructionMonitoringArea;
      }
      cmStore.room = room?.constructionRoom;
    } catch (err) {
      console.error('could not fetch construction monitoring data', err);
    } finally {
      cmStore.isLoading = false;
    }
  };

  const getCMBreadcrumb = computed(() => {
    if (!cmStore || IS_SSR || !window.location.href.includes(pageUrl)) {
      return null;
    }

    const breadcrumbs = [];

    if (IS_SHOWROOM && window.location.href.includes(pageUrl)) {
      breadcrumbs.push({
        url: pageUrl,
        title:
          dict.get('construction-Projects.Breadcrumb.Construction-Monitoring') ||
          'Construction Monitoring',
      });
    }

    if (cmStore.project) {
      breadcrumbs.push({
        url: pageUrl + getPathString('project'),
        title: cmStore.project.name,
      });
    }

    if (cmStore.area) {
      breadcrumbs.push({
        url: pageUrl + getPathString('project') + getPathString('area'),
        title: cmStore.area.name,
      });
    }

    if (cmStore.room) {
      breadcrumbs.push({
        url: pageUrl + getPathString('project') + getPathString('area') + getPathString('room'),
        title: cmStore.room.name,
      });
    }

    if (cmStore.isManageParticipantsPage && !cmStore.isLoading) {
      // url can be empty because always last element
      breadcrumbs.push({
        url: '',
        title:
          dict.get('construction-Projects.Breadcrumb.Manage-Participants') || 'Manage Participants',
      });
    }
    return breadcrumbs;
  });

  const refetchProject = async () => {
    try {
      await loadProject();
      const result = await doRefetchProject();
      cmStore.project = { ...result?.data?.constructionSite, areas: cmStore.project?.areas };
    } catch (err) {
      console.error('could not fetch construction monitoring project data', err);
    }
  };

  const refetchArea = async () => {
    try {
      await loadArea();
      const result = await doRefetchArea();
      cmStore.area = { ...result?.data?.constructionMonitoringArea, rooms: cmStore.area?.rooms };
    } catch (err) {
      console.error('could not refetch construction monitoring area', err);
    }
  };

  const refetchAreaList = async () => {
    try {
      await loadAreaList();
      const result = await doRefetchAreaList();
      if (cmStore.project && result?.data?.constructionMonitoringAreaList?.areas) {
        cmStore.project.areas = result.data.constructionMonitoringAreaList.areas;
      }
    } catch (err) {
      console.error('could not refetch area list', err);
    }
  };

  const refetchRoom = async () => {
    try {
      await loadRoom();
      const result = await doRefetchRoom();
      cmStore.room = result?.data?.constructionRoom;
    } catch (err) {
      console.error('could not refetch construction documentation area', err);
    }
  };

  const refetchRoomList = async () => {
    try {
      await loadRoomList();
      const result = await doRefetchRoomList();
      if (cmStore.area && result?.data?.constructionRoomList?.rooms) {
        cmStore.area.rooms = result.data.constructionRoomList.rooms;
      }
    } catch (err) {
      console.error('could not refetch room list', err);
    }
  };

  const refetchSensorBoltSummary = async () => {
    try {
      await loadSensorBolts();
      const result = await refetchSensorBolts();
      if (cmStore.sensorBoltSummary && result?.data?.sensorBoltSummary?.items) {
        cmStore.sensorBoltSummary = result.data.sensorBoltSummary.items;
      }
    } catch (err) {
      console.error('could not refetch sensor bolt summary', err);
    }
  };

  const refetchSensorDiscSummary = async () => {
    try {
      await loadSensorDiscs();
      const result = await refetchSensorDiscs();
      if (cmStore.sensorDiscSummary && result?.data?.sensorDiscSummary?.items) {
        cmStore.sensorDiscSummary = result.data.sensorDiscSummary.items;
      }
    } catch (err) {
      console.error('could not refetch sensor disc summary', err);
    }
  };

  return {
    pageUrl,
    setBrowserPageTitle,
    loadProject,
    refetchProject,
    projectLoading,
    loadArea,
    refetchArea,
    loadAreaList,
    areaLoading,
    refetchAreaList,
    areaListLoading,
    loadRoom,
    refetchRoom,
    roomLoading,
    loadRoomList,
    refetchRoomList,
    roomListLoading,
    loadSensorBoltSummary,
    refetchSensorBoltSummary,
    sensoBoltSummaryLoading,
    loadSensorDiscSummary,
    refetchSensorDiscSummary,
    sensoDiscSummaryLoading,
    getCMUrl,
    loadConstructionMonitoringData,
    getCMBreadcrumb,
  };
};
